import { gravComponentSchema } from '../deep-form-grav';
import { AdvancedRankProgressProps, AdvancedMetricDefinition, AdvancedRankLevel, advancedDefaults } from '@/components/ui/RankProgressAdvanced';

type WithSlots = AdvancedRankProgressProps & {
    '#default': string;
    '#after': string;
};

const { s, l, bl, c, v, src, toExport } = gravComponentSchema<WithSlots>({
    title: 'RankProgressAdvanced',
    link: 'https://docs.ds.mlmsoft.cloud/doc/rankprogressadvanced-hGDFGhTng7',
    text: text(),
}, advancedDefaults);

function text() {
    return `<b>UI Component: Advanced Rank Progress</b><br/>This component uses expressions for metric values and rank thresholds.`;
}


s.add('title').input('text').title('Title').col(8);
s.add('treeId').as(l.clone('selectTree')).title('Tree ID').col(2);
s.add('nextRankTemplate').input('text').title('Next Rank Template').col(6);
s.add('earnedRankTemplate').input('text').title('Earned Rank Template').col(6);
s.add('styleDefaults').input('dform').data([
    l.spawn<AdvancedMetricDefinition['type']>('type').input('dictionary').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).data(['percent', 'percent-outside']).title('Type').col(3).item(),

    l.spawn<string>('color').input('color').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Color').col(3).item(),
    l.spawn<string>('bgColor').input('color').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Background Color').col(3).item(),
    l.spawn<string>('colorDone').input('color').props({ density: 'compact', dfTitleInside: true }).title('Color Done').col(3).item(),
    l.spawn<number>('size').as(bl.clone('number')).props({ class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Bar Height (px)').col(3).item(),
    l.spawn<string>('titleClass').input('text').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Title Class').col(3).item(),
    l.spawn<string>('labelClass').input('text').props({ density: 'compact', dfTitleInside: true }).title('Label Class').col(3).item(),
]).title('Default Metric Styles').col(12);

s.add('metrics').input('dlist').data(
    s.spawn<AdvancedMetricDefinition>().input('dform').data([
        l.spawn<string>('metric').input('text').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Metric Key (Marketing property, e.g m.PV)').mandatory().col(6).item(),
        l.spawn<string>('title').input('text').props({ density: 'compact', dfTitleInside: true }).title('Title').mandatory().col(6).item(),
        l.spawn<string>('value').input('text').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Expression: current value').mandatory().col(6).item(),
        l.spawn<string>('max').input('text').props({ density: 'compact', dfTitleInside: true }).title('Expression: max value').col(6).item(),
        l.spawn<AdvancedMetricDefinition['type']>('type').input('dictionary').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).data(['percent', 'percent-outside']).title('Type').col(3).item(),
        l.spawn<string>('color').input('color').props({ class: 'mr-md-2',density: 'compact', dfTitleInside: true }).title('Color').col(3).item(),
        l.spawn<string>('bgColor').input('color').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Background color').col(3).item(),
        l.spawn<string>('colorDone').input('color').props({ density: 'compact', dfTitleInside: true }).title('Color done').col(3).item(),

        l.spawn<string>('titleClass').input('text').props({class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Title class').col(3).item(),
        l.spawn<string>('labelClass').input('text').props({ class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Label class').col(3).item(),
      l.spawn<number>('size').as(bl.clone('number')).props({  class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Bar Height (px)').col(3).item(),
        l.spawn<boolean>('labelIsPercent').input('boolean').props({  density: 'compact', dfTitleInside: true }).title('Show percent').col(3).item(),
      l.spawn<boolean>('rounded').input('boolean').props({ class: 'mr-md-2', density: 'compact', dfTitleInside: true }).title('Rounded').col(3).item(),
        l.spawn<boolean>('striped').input('boolean').props({ density: 'compact', dfTitleInside: true }).title('Striped').col(3).item(),

    ])
        .pre((v) => typeof v === 'string' ? { metric: v, value: '', type: 'percent' } : v)
        .init(() => ({ metric: '', value: '', type: 'percent' }))
        .props({ style: 'border: 1px solid #ccc; padding: 20px; margin: 10px; border-radius: 5px;' })
        .item()
).title('Fields').col(12);

const selectDefinedProperty = s.spawn<string>()
    .as(l.clone('propertyMarketing')); // replace with dependent property


const levelItem = s.spawn().input('dform').data([
    l.spawn<string>('key').asMany([l.clone('itemLeft'), selectDefinedProperty])
        .title('Marketing property').mandatory()
        .props({ density: 'compact', dfTitleInside: true })
        .col(9).item(),
    l.spawn<string>('value').asMany([l.clone('itemRight'), bl.clone('string')])
        .title('Required value').mandatory()
        .props({ density: 'compact', dfTitleInside: true })
        .col(3).item(),
]).init(() => ({ key: '', value: '' }));


const rankDefinition = s.spawn<AdvancedRankLevel>().input('dform').data([
  s.spawn<string>('title').input('text')
    .title('Rank name').mandatory()
    .props({ class: 'mr-md-2', density: 'compact', dfTitleInside: true })
    .col(3).item(),
  s.spawn<string>('id').as(bl.clone('number')).props({ class: 'mr-md-2',density: 'compact', dfTitleInside: true }).title('Rank ID').mandatory().col(3).item(),
  s.spawn<string>('nextRank').as(bl.clone('number')).props({ density: 'compact', dfTitleInside: true }).title('Next Rank ID').mandatory().col(3).item(),
  s.spawn<Record<string, string>>('levels').input('drecord').data(levelItem.item()).col(12).item(),
  s.spawn<string>('description').input('vtextarea').props({ clearable: true }).title('HTML Description').col(6).item()

])
  .props({ style: 'border: 1px solid #ccc; padding: 10px; margin-bottom: 10px; background-color: rgba(194,194,194,0.3);' })
  .init(() => ({  id: 0, title: '', levels: {} }));


s.add('ranks').input('dlist').data(rankDefinition.item())
    .title('Ranks')
    .desc('Ranks calculation rules. Make sure ranks are sorted by required values in ascending order. Lowest ranks must be higher up in this table.')
    .col(12);


s.add('#default').input('vtextarea').props({ clearable: true })
    .flag('isSlot').flag('isText').flag('slotParams', 'sp')
    .title('Text before').col(12);

s.add('#after').input('vtextarea').props({ clearable: true })
    .flag('isSlot').flag('isText').flag('slotParams', 'sp')
    .title('Text after').col(12);

export default toExport;
